import { Link, useLoaderData } from "react-router";
import type { LoaderFunctionArgs } from "react-router";
import { buildMetadata } from "@/lib/seo";
import type { Project } from "@/data/projects";

import type { SiteContent } from "@/content";

import { ArrowRight, ButtonLink } from "@/components/ui/Button";
import { MediaFigure } from "@/components/ui/MediaFigure";
import { PageHeader } from "@/components/ui/PageHeader";
import { Reveal, RevealGroup } from "@/components/ui/Reveal";
import { Section, SectionHeading } from "@/components/ui/Section";
import { imageReveal } from "@/lib/motion";

type LoaderData = { project: Project; others: Project[] };

/**
 * The project body and gallery are not in the bootstrap payload, so this route
 * fetches its own document. An unknown slug throws a 404 Response rather than
 * rendering an empty page, which is what notFound() did before.
 */
export async function loader({ params, context, request }: LoaderFunctionArgs): Promise<LoaderData> {
  const { fetchProject, fetchSiteContent } = await import("@/content");

  // Context exists on the server and not in the browser. See the note in
  // stories.$slug.tsx: assuming it does is what broke client-side navigation.
  const content = (context as { content?: SiteContent } | undefined)?.content ?? (await fetchSiteContent(request.signal));

  const project = await fetchProject(String(params.slug), request.signal);
  if (!project) throw new Response("No project with that slug.", { status: 404 });

  return { project, others: content.projects.filter((p) => p.slug !== project.slug).slice(0, 3) };
}

export const meta = ({ data }: { data?: LoaderData }) =>
  data
    ? buildMetadata({
        title: data.project.metaTitle ?? data.project.title,
        description: data.project.summary,
        path: `/projects/${data.project.slug}`,
      })
    : null;

export function ProjectPage() {
  const { project, others } = useLoaderData<LoaderData>();

  return (
    <>
      <PageHeader
        eyebrow={project.pillar}
        title={project.title}
        lead={project.summary}
        trail={[
          { name: "Home", href: "/" },
          { name: "Our Work", href: "/our-work" },
          { name: project.title, href: `/projects/${project.slug}` },
        ]}
      />

      <Section>
        <Reveal variants={imageReveal}>
          <MediaFigure
            item={project.image}
            ratio="16 / 9"
            sizes="(min-width: 1280px) 1180px, 100vw"
            showCaption
          />
        </Reveal>

        <div className="mt-14 grid gap-12 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)] lg:gap-16">
          <Reveal>
            <div className="prose-body max-w-2xl" style={{ fontSize: "var(--step-lead)" }}>
              {project.body.map((paragraph) => (
                <p key={paragraph.slice(0, 40)} className="text-ink">
                  {paragraph}
                </p>
              ))}
            </div>
          </Reveal>

          <Reveal>
            <div className="rounded-[16px] border border-line bg-mist p-6">
              <h2 className="text-[0.8125rem] font-bold uppercase tracking-[0.14em] text-blue">
                Project detail
              </h2>
              <dl className="mt-5 space-y-4">
                <div>
                  <dt className="text-[0.875rem] text-muted">Status</dt>
                  <dd className="font-semibold text-ink">{project.status}</dd>
                </div>
                <div>
                  <dt className="text-[0.875rem] text-muted">Location</dt>
                  <dd className="font-semibold text-ink">{project.location}</dd>
                </div>
                <div>
                  <dt className="text-[0.875rem] text-muted">Period</dt>
                  <dd className="font-semibold text-ink">{project.period}</dd>
                </div>
                <div>
                  <dt className="text-[0.875rem] text-muted">Programme pillar</dt>
                  <dd className="font-semibold text-ink">
                    {project.pillarSlug === "get-involved" ? (
                      project.pillar
                    ) : (
                      <Link
                        to={`/our-work/${project.pillarSlug}`}
                        className="text-navy underline underline-offset-4"
                      >
                        {project.pillar}
                      </Link>
                    )}
                  </dd>
                </div>
              </dl>

              <h2 className="mt-8 text-[0.8125rem] font-bold uppercase tracking-[0.14em] text-blue">
                Reported figures
              </h2>
              <dl className="mt-4 space-y-3">
                {project.facts.map((fact) => (
                  <div key={fact.label} className="border-b border-line pb-3 last:border-0 last:pb-0">
                    <dt className="text-[0.875rem] text-muted">{fact.label}</dt>
                    <dd className="font-serif text-[1.0625rem] text-midnight">{fact.value}</dd>
                  </div>
                ))}
              </dl>

              {project.partners && project.partners.length > 0 ? (
                <>
                  <h2 className="mt-8 text-[0.8125rem] font-bold uppercase tracking-[0.14em] text-blue">
                    In partnership with
                  </h2>
                  <ul className="mt-4 space-y-2">
                    {project.partners.map((partner) => (
                      <li key={partner.name}>
                        {partner.href ? (
                          <a
                            href={partner.href}
                            target="_blank"
                            rel="noreferrer noopener"
                            className="font-semibold text-navy underline underline-offset-4"
                          >
                            {partner.name}
                          </a>
                        ) : (
                          <span className="font-semibold text-ink">{partner.name}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </>
              ) : null}

              <p className="mt-8 border-t border-line pt-5 text-[0.8125rem] leading-relaxed text-muted">
                Source:{" "}
                <a
                  href={project.source}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="text-navy underline underline-offset-4"
                >
                  himshikharanepal.org.np
                </a>
              </p>
            </div>
          </Reveal>
        </div>
      </Section>

      {project.gallery.length > 0 ? (
        <Section tone="ivory" ariaLabelledBy="project-gallery-heading">
          <SectionHeading
            id="project-gallery-heading"
            eyebrow="From this project"
            title="Photographs"
          />
          <RevealGroup as="ul" className="mt-10 grid gap-6 sm:grid-cols-2">
            {project.gallery.map((media) => (
              <Reveal as="li" key={media.id}>
                <MediaFigure item={media} ratio="4 / 3" sizes="(min-width: 640px) 45vw, 100vw" />
              </Reveal>
            ))}
          </RevealGroup>
        </Section>
      ) : null}

      <Section tone="mist" ariaLabelledBy="other-projects-heading">
        <SectionHeading id="other-projects-heading" eyebrow="Elsewhere" title="Other projects" />
        <RevealGroup as="ul" className="mt-10 grid gap-6 md:grid-cols-3">
          {others.map((other) => (
            <Reveal as="li" key={other.slug}>
              <div className="h-full rounded-[16px] border border-line bg-white p-6">
                <p className="text-[0.8125rem] font-semibold uppercase tracking-[0.1em] text-blue">
                  {other.pillar}
                </p>
                <h3 className="mt-3" style={{ fontSize: "var(--step-h4)" }}>
                  {other.title}
                </h3>
                <p className="mt-2.5 text-[0.9375rem] text-muted">{other.summary}</p>
                <Link
                  to={`/projects/${other.slug}`}
                  className="mt-5 inline-flex min-h-11 items-center gap-2 text-[0.9375rem] font-semibold text-navy"
                >
                  Read about {other.title}
                  <ArrowRight />
                </Link>
              </div>
            </Reveal>
          ))}
        </RevealGroup>

        <div className="mt-10">
          <ButtonLink href="/get-involved/support" variant="secondary">
            Support this work
            <ArrowRight />
          </ButtonLink>
        </div>
      </Section>
    </>
  );
}
